import type { PageTemplate } from '@packages/recommendations/src/types';
import type { ParagraphLibraryType, ParagraphType } from '../../../interfaces/components';
import { ComponentTypes } from '../../../interfaces/components';
import { CookieConsentManager } from '../CookieConsentManager/CookieConsentManager.client';
import { classes } from '../../utils/classes';
import { getComponent } from './componentRegistry';
import { RefreshContentAfterLogin } from '../CmsContent/RefreshContentAfterLogin.client';

const classesDistance = (item: ParagraphType) => {
  const distanceBehaviour =
    'behavior_paragraph_widthtype' in item && item.behavior_paragraph_widthtype;

  const distanceField = 'field_distance' in item && item.field_distance;

  const distance = distanceBehaviour || distanceField;

  switch (distance) {
    case 'fullwidth': {
      // complete browser window width
      return 'relative left-1/2 w-screen -translate-x-1/2! translate-y-0';
    }
    case 'default': {
      // yes, "default" is actually not the default. The default is an unset value (further below).
      // website width. Put it into a centered container.
      return 'max-w-full xl:container xl:mx-auto';
    }
    case 'variation-1': {
      // website width with additional mobile padding
      return 'max-w-full xl:container xl:mx-auto px-3 lg:px-0';
    }
    default: // no setting
      return '';
  }
};

type ComponentRecognizerProps = {
  item: ParagraphType;
  pageTemplate?: PageTemplate;
  recoIndex?: number;
  token?: string;
  className?: string;
  style?: React.CSSProperties;
};

export const ComponentRecognizer = ({
  /** The data for the single paragraph you want to render */
  item,
  /** current page you are on (needed for tracking purposes) */
  pageTemplate,
  /** For tracking: if this component is a Reco, please specify which reco it is on the page (starting with 0) */
  recoIndex = 0,
  token,
  className,
  style,
}: ComponentRecognizerProps) => {
  const Component = getComponent(item.type);
  if (Component) {
    return (
      <Component
        data={item}
        className={classes(classesDistance(item), className)}
        pageTemplate={pageTemplate}
        recoIndex={recoIndex}
        token={token}
        key={item.id}
        style={style}
      />
    );
  }

  if ((item.type as ComponentTypes) === ComponentTypes.FromLibrary) {
    return (
      <>
        {(item as ParagraphLibraryType)?.paragraphs_library_item?.map((libItem) =>
          libItem?.paragraph?.map((libParagraphItem: ParagraphType) => (
            <ComponentRecognizer key={item.id} item={libParagraphItem} token={token} />
          )),
        )}
      </>
    );
  }
  return null;
};

/**
 * Renders an array of cms paragraphs using the correct components from the component recognizer
 */
export const CmsComponents = ({
  paragraphs = [],
  pageTemplate,
  token,
}: {
  /** array of all paragraphs that are to be rendered */
  paragraphs: ParagraphType[];
  /** current page you are on (needed for tracking purposes) */
  pageTemplate: PageTemplate;
  token?: string;
}) => {
  let recoIndex = 0;

  return (
    <CookieConsentManager>
      <RefreshContentAfterLogin />
      {paragraphs.map((p) => (
        <ComponentRecognizer
          key={p.id}
          item={p}
          pageTemplate={pageTemplate}
          // if this paragraph is a recoslider, pass the reco index and increase it after that
          {...((p.type as ComponentTypes) === ComponentTypes.RecoPrudsys && {
            // eslint-disable-next-line no-plusplus
            recoIndex: recoIndex++,
          })}
          token={token}
        />
      ))}
    </CookieConsentManager>
  );
};
